import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement, ReactNode } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';
import type { TCreateDatasourceResult } from '../../interfaces/IAiChatTools';

/**
 * The `createDatasource` tool call: rows the model has itself, stored as a datasource of the chat.
 *
 * A step row, collapsed: the header names the call and shows the purpose; the count, or why the
 * rows were not stored, waits behind the toggle. The rows are never drawn here — they are in the
 * input, but a display tool is where the model places them, read from the route by id like any
 * fetched result.
 */
function CreateDatasourceToolCall({ part }: IAiChatToolCallOf<'createDatasource'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('createDatasource');
  const status = useAiChatToolStatus(part.state);

  const output = part.state === 'output-available' ? part.output : undefined;

  const readSummary = (result: TCreateDatasourceResult): string => (
    result.created
      ? t('aiChat.tools.datasourceCreated', { count: result.rowCount })
      : t('aiChat.tools.datasourceNotCreated')
  );

  const readDetails = (result: TCreateDatasourceResult): ReactNode => (
    result.created ? (
      <>
        <p>{t('aiChat.tools.dataRowsStored', { count: result.rowCount })}</p>
        <p>{t('aiChat.tools.datasourceColumns', { columns: result.columns.map(({ name }) => name).join(', ') })}</p>
      </>
    ) : (
      <p>{result.reason}</p>
    )
  );

  return (
    <AiChatToolCard
      name={label}
      status={output ? readSummary(output) : status}
      details={output ? readDetails(output) : undefined}
      errorText={part.errorText}
    >
      {part.input?.purpose}
    </AiChatToolCard>
  );
}

export default CreateDatasourceToolCall;
